import { useEffect, useRef, useState } from "react";
import styles from "./EvaluationGroupActionModal.module.css";
import ModalView from "@modals/modalview/ModalView";
import closeIcon from "@assets/icons/close-icon.svg";
import arrDownIcon from "@assets/icons/arr_down.svg";
import {
  useAssignEvaluationTaskByPhase,
  useGetEvaluatorPhases,
} from "@services/evaluationService";
import { toast } from "sonner";

interface EvaluationGroupActionModalProps {
  isOpen: boolean;
  groupName: string;
  evaluationGroupId: string;
  onProceed: (evaluationPhaseId: string) => void;
  onClose: () => void;
}

function EvaluationGroupActionModal({
  isOpen,
  groupName,
  evaluationGroupId,
  onProceed,
  onClose,
}: EvaluationGroupActionModalProps) {
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    mutateAsync: assignTaskByPhase,
    isPending: isAssigningPhase,
  } = useAssignEvaluationTaskByPhase();

  const {
    data: phasesData,
    isLoading: isPhasesLoading,
    error: phasesError,
    refetch: refetchPhases,
  } = useGetEvaluatorPhases(evaluationGroupId, isOpen);

  const phaseOptions = phasesData?.evaluation_phases ?? [];

  useEffect(() => {
    if (!isOpen) {
      setSelectedPhaseId(null);
      setIsDropdownOpen(false);
      return;
    }

    setSelectedPhaseId(null);
    setIsDropdownOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const selectedLabel = isPhasesLoading
    ? "Loading options..."
    : (phaseOptions.find((phase) => phase.id === selectedPhaseId)?.label ??
      "Select An Option");

  const handleSelectPhase = (phaseId: string) => {
    setSelectedPhaseId(phaseId);
    setIsDropdownOpen(false);
  };

  const handleProceed = async () => {
    if (!selectedPhaseId || isAssigningPhase) return;

    try {
      await assignTaskByPhase(selectedPhaseId);
      onProceed(selectedPhaseId);
    } catch {
      toast.error("Failed to assign evaluation task. Please try again.");
    }
  };

  const isDropdownDisabled =
    isPhasesLoading ||
    isAssigningPhase ||
    Boolean(phasesError) ||
    phaseOptions.length === 0;

  return (
    <ModalView isOpen={isOpen} onClose={onClose}>
      <div className={styles.container}>
        <div className={styles.closeHeader}>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <img src={closeIcon} alt="" />
          </button>
        </div>

        <h2 className={styles.title}>{groupName}</h2>
        <p className={styles.subtitle}>
          Choose an action for this evaluation group.
        </p>

        <div className={styles.selectWrapper} ref={dropdownRef}>
          <div
            className={`${styles.selectBox} ${
              isDropdownOpen ? styles.selectBoxOpen : styles.selectBoxClosed
            }`}
          >
            <button
              type="button"
              className={styles.selectTrigger}
              onClick={() => !isDropdownDisabled && setIsDropdownOpen((open) => !open)}
              aria-expanded={isDropdownOpen}
              aria-haspopup="listbox"
              disabled={isDropdownDisabled}
            >
              <span
                className={
                  selectedPhaseId ? styles.selectedText : styles.placeholderText
                }
              >
                {selectedLabel}
              </span>
              <img
                src={arrDownIcon}
                alt=""
                className={`${styles.arrDownIcon} ${
                  isDropdownOpen ? styles.arrDownIconOpen : ""
                }`}
              />
            </button>

            {phasesError && (
              <p className={styles.phasesError}>
                Failed to load options.{" "}
                <button type="button" onClick={() => refetchPhases()}>
                  Retry
                </button>
              </p>
            )}

            {!isPhasesLoading && !phasesError && phaseOptions.length === 0 && (
              <p className={styles.phasesEmpty}>No actions available.</p>
            )}

            <ul
              className={styles.optionsList}
              role="listbox"
              aria-hidden={!isDropdownOpen}
            >
              {phaseOptions.map((phase) => (
                <li key={phase.id} role="option">
                  <button
                    type="button"
                    className={styles.optionButton}
                    onClick={() => handleSelectPhase(phase.id)}
                    tabIndex={isDropdownOpen ? 0 : -1}
                    disabled={isAssigningPhase}
                  >
                    {phase.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={styles.btnContainer}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.proceedBtn} ${
              selectedPhaseId ? styles.proceedBtnEnabled : ""
            }`}
            onClick={handleProceed}
            disabled={!selectedPhaseId || isPhasesLoading || isAssigningPhase}
          >
            {isAssigningPhase ? "Assigning..." : "Proceed"}
          </button>
        </div>
      </div>
    </ModalView>
  );
}

export default EvaluationGroupActionModal;
